import {
  HFD_FEF9_DONGLE_ID_PREFIX,
  HFD_FEF9_DONGLE_PRODUCT_ID,
  HFD_FEF9_DONGLE_X87_NEON_IDENTIFIER,
  isHfdFef9DongleProductId,
  isHfdFef9DongleX87NeonIdentifier,
} from "./hfdFef9Dongle";

/** HFD X87 Neon 有线（USB）。keyboardType 复用 huafenda87，靠 identifier / PID 与经典 X87、X87 V2 区分。 */
export const HFD_X87_NEON_WIRED_IDENTIFIER = "3141:34820:X87 Neon";

/**
 * HFD X87 Neon 2.4G。
 * 与 X87 V2 共用接收器 VID/PID 0xfef9，只能靠全量 identifier 识别。
 */
export const HFD_X87_NEON_WIRELESS_IDENTIFIER =
  HFD_FEF9_DONGLE_X87_NEON_IDENTIFIER;

export const HFD_X87_NEON_VENDOR_PRODUCT: ReadonlyArray<{
  vendorId: number;
  productId: number;
  identifier: string;
}> = [
  {
    vendorId: 0x0c45,
    productId: 0x8804,
    identifier: HFD_X87_NEON_WIRED_IDENTIFIER,
  },
  {
    vendorId: 0x0c45,
    productId: HFD_FEF9_DONGLE_PRODUCT_ID,
    identifier: HFD_X87_NEON_WIRELESS_IDENTIFIER,
  },
];

export type HfdX87NeonEquipmentLike = {
  vendorId?: number;
  productId?: number;
  keyboardType?: string;
  displayname?: string;
  productName?: string;
  solutionRovider?: string;
  identifier?: string;
};

function x87NeonNameBlob(eq: HfdX87NeonEquipmentLike): string {
  return `${eq.displayname ?? ""} ${eq.productName ?? ""} ${eq.identifier ?? ""}`;
}

function isFef9IdentifierFamily(identifier: string): boolean {
  return identifier.startsWith(HFD_FEF9_DONGLE_ID_PREFIX);
}

/** 是否为 HFD X87 Neon（有线 0x8804 + 2.4G 全量 id `…Dongle:3:10`） */
export function isHuafendaX87NeonDevice(
  eq: HfdX87NeonEquipmentLike | null | undefined
): boolean {
  if (!eq) return false;

  const pid = Number(eq.productId);
  const id = String(eq.identifier ?? "").trim();

  // 有线：独立 PID
  if (pid === 0x8804) return true;

  // 2.4G：与 V2 同 VID/PID，只能认全量 identifier
  if (isHfdFef9DongleProductId(pid) || isFef9IdentifierFamily(id)) {
    return isHfdFef9DongleX87NeonIdentifier(id);
  }

  if (
    id === HFD_X87_NEON_WIRED_IDENTIFIER ||
    id.startsWith(`${HFD_X87_NEON_WIRED_IDENTIFIER}:`)
  ) {
    return true;
  }
  if (id.includes("34820") && /x87\s*neon/i.test(id)) return true;

  // 名称兜底仅用于有线场景；fef9 族上面已 return
  if (/\bx87\s*neon\b/i.test(x87NeonNameBlob(eq))) return true;

  const vid = Number(eq.vendorId);
  if (!Number.isFinite(vid) || !id) return false;
  return HFD_X87_NEON_VENDOR_PRODUCT.some(
    (p) =>
      p.productId !== HFD_FEF9_DONGLE_PRODUCT_ID &&
      p.vendorId === vid &&
      (p.identifier === id || id.startsWith(`${p.identifier}:`))
  );
}

/** 是否为 HFD X87 Neon（方案商为华奋达，或未标方案商） */
export function isHuafendaX87NeonEquipment(
  eq: HfdX87NeonEquipmentLike | null | undefined
): boolean {
  if (!eq || typeof eq !== "object") return false;
  const provider = String(eq.solutionRovider ?? "")
    .trim()
    .toLowerCase();
  if (provider && provider !== "huafenda") return false;
  return isHuafendaX87NeonDevice(eq);
}

/** 是否为 HFD X87 Neon 有线设备 */
export function isHuafendaX87NeonWired(
  eq: HfdX87NeonEquipmentLike | null | undefined
): boolean {
  return isHuafendaX87NeonDevice(eq) && Number(eq?.productId) === 0x8804;
}

/** 是否为 HFD X87 Neon 2.4G 设备（全量 identifier 判定） */
export function isHuafendaX87NeonWireless(
  eq: HfdX87NeonEquipmentLike | null | undefined
): boolean {
  return isHfdFef9DongleX87NeonIdentifier(eq?.identifier);
}
